/**
 * Modal.jsx
 * FILE VERSION: MODAL-v1.7.41.36
 * Ledger #1573
 *
 * The shared modal CHARLIE never had.
 *
 * Real cost of not having one, already paid: #1557 needed a single
 * standard behaviour -- click the backdrop to close -- and it took edits
 * at 23 separate sites across 8 files, because the backdrop markup is
 * copy-pasted everywhere. Every future modal-level change carries that
 * same 23x cost, and any site that gets missed silently behaves
 * differently from the rest.
 *
 * OWNS THE BEHAVIOURS THAT GET FORGOTTEN when a modal is hand-rolled:
 *   - backdrop click to close
 *   - Escape to close
 *   - body scroll lock (a modal over a page that scrolls behind it is a
 *     real annoyance nobody remembers to fix)
 *   - focus moved into the dialog on open
 *   - stopPropagation on the panel, so clicking a form field inside
 *     never dismisses it -- the bug that would have been worse than the
 *     one #1557 fixed
 *
 * Deliberately NOT a redesign. It reproduces the existing look exactly,
 * so adoption is a safe swap rather than a visual change smuggled in
 * alongside a refactor. Restyling is #1557's other half and is Stinger's
 * design call, not something to slip in here.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import Button from './Button';
import { useBackToClose } from '../lib/useBackToClose';

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width = 440,
  closeOnBackdrop = true,
  closeOnEscape = true,
  showClose = true,
  zIndex = 200,
}) {
  const panelRef = useRef(null);
  useBackToClose(open, onClose, 1); // Back closes the modal instead of leaving the page (WO #2440)

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEscape) onClose?.();
  }, [closeOnEscape, onClose]);

  useEffect(() => {
    if (!open) return;

    // Scroll lock. Restores the previous value rather than assuming
    // 'visible' -- another modal or a drawer may already have locked it,
    // and clobbering that would unlock the page underneath.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    document.addEventListener('keydown', handleEscape);

    // Move focus into the dialog so Escape and tabbing work without the
    // user clicking first.
    const t = setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleEscape);
      clearTimeout(t);
    };
  }, [open, handleEscape]);

  if (!open) return null;

  return (
    <div
      onClick={closeOnBackdrop ? onClose : undefined}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex, padding: 16,
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        // The guard that matters: without this, every click on a field
        // inside the modal would hit the backdrop handler and dismiss it.
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#ffffff', borderRadius: 10, border: '1px solid var(--gov-slate-200)',
          padding: 24, width, maxWidth: '95vw', maxHeight: '90vh',
          overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          outline: 'none',
        }}
      >
        {(title || showClose) && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 12 }}>
            {typeof title === 'string'
              ? <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gov-text-primary)' }}>{title}</div>
              : title}
            {showClose && (
              <Button onClick={onClose} iconOnly aria-label="Close" style={{ fontSize: 20, lineHeight: 1 }}>
                ×
              </Button>
            )}
          </div>
        )}

        {children}

        {footer && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
